// src/components/policies/PolicyForm.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { policiesService } from '../../services/policies';
import Loading from '../common/Loading';

const PolicyForm = ({ mode = 'create' }) => {
  const navigate = useNavigate();
  const { policyId } = useParams();
  const [loading, setLoading] = useState(mode === 'edit');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [rules, setRules] = useState([{ name: '', condition: '', action: 'Alert', description: '' }]);
  const [scopeIdentifiers, setScopeIdentifiers] = useState(['']);
  
  const [formData, setFormData] = useState({
    policy_name: '',
    description: '',
    policy_type: '',
    scope_type: 'global',
    scope_identifiers: [],
    is_enabled: true,
    action_on_violation: 'AlertOnly',
    rules: []
  });

  // If editing, fetch the policy data
  useEffect(() => {
    if (mode === 'edit' && policyId) {
      const fetchPolicy = async () => {
        try {
          setLoading(true);
          const data = await policiesService.getPolicy(policyId);
          setFormData(data);
          
          // Set rules array
          if (data.rules && data.rules.length > 0) {
            setRules(data.rules);
          }
          
          // Set scope identifiers
          if (data.scope_identifiers && data.scope_identifiers.length > 0) {
            setScopeIdentifiers(data.scope_identifiers);
          }
          
          setError(null);
        } catch (err) {
          console.error('Error fetching policy:', err);
          setError('Failed to load policy data. Please try again.');
        } finally {
          setLoading(false);
        }
      };

      fetchPolicy();
    }
  }, [mode, policyId]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleRuleChange = (index, field, value) => {
    const updatedRules = [...rules];
    updatedRules[index] = {
      ...updatedRules[index],
      [field]: value
    };
    setRules(updatedRules);
  };

  const addRule = () => {
    setRules([...rules, { name: '', condition: '', action: 'Alert', description: '' }]);
  };

  const removeRule = (index) => {
    const updatedRules = rules.filter((_, i) => i !== index);
    setRules(updatedRules);
  };

  const handleScopeIdentifierChange = (index, value) => {
    const updatedIdentifiers = [...scopeIdentifiers];
    updatedIdentifiers[index] = value;
    setScopeIdentifiers(updatedIdentifiers);
  };

  const addScopeIdentifier = () => {
    setScopeIdentifiers([...scopeIdentifiers, '']);
  };

  const removeScopeIdentifier = (index) => {
    const updatedIdentifiers = scopeIdentifiers.filter((_, i) => i !== index);
    setScopeIdentifiers(updatedIdentifiers);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSubmitting(true);
      setError(null);
      
      // Filter out empty rules
      const filteredRules = rules.filter(rule => rule.name.trim() !== '' && rule.condition.trim() !== '');
      
      // Filter out empty scope identifiers
      const filteredIdentifiers = scopeIdentifiers.filter(id => id.trim() !== '');
      
      // Prepare the data for submission
      const policyData = {
        ...formData,
        rules: filteredRules,
        scope_identifiers: filteredIdentifiers
      };
      
      if (mode === 'edit') {
        await policiesService.updatePolicy(policyId, policyData);
      } else {
        await policiesService.createPolicy(policyData);
      }
      
      // Redirect to policies list on success
      navigate('/policies');
    } catch (err) {
      console.error('Error saving policy:', err);
      setError(`Failed to ${mode === 'edit' ? 'update' : 'create'} policy. Please check your input and try again.`);
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Loading text="Loading policy data..." />;
  }

  return (
    <div className="bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6">
      <div className="md:grid md:grid-cols-3 md:gap-6">
        <div className="md:col-span-1">
          <h3 className="text-lg font-medium leading-6 text-gray-900">
            {mode === 'edit' ? 'Edit Security Policy' : 'Create New Security Policy'}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {mode === 'edit' 
              ? 'Update the settings and rules for this security policy.' 
              : 'Define a new security policy to protect your OT assets.'}
          </p>
        </div>
        <div className="mt-5 md:mt-0 md:col-span-2">
          {error && (
            <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          )}
          
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-6 gap-6">
              {/* Policy Name */}
              <div className="col-span-6 sm:col-span-4">
                <label htmlFor="policy_name" className="block text-sm font-medium text-gray-700">
                  Policy Name *
                </label>
                <input
                  type="text"
                  name="policy_name"
                  id="policy_name"
                  required
                  value={formData.policy_name}
                  onChange={handleChange}
                  className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                />
              </div>

              {/* Policy Type */}
              <div className="col-span-6 sm:col-span-3">
                <label htmlFor="policy_type" className="block text-sm font-medium text-gray-700">
                  Policy Type *
                </label>
                <select
                  id="policy_type"
                  name="policy_type"
                  required
                  value={formData.policy_type}
                  onChange={handleChange}
                  className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value="">Select type</option>
                  <option value="NetworkTraffic">Network Traffic</option>
                  <option value="BehavioralAnalysis">Behavioral Analysis</option>
                  <option value="Firewall">Firewall</option>
                  <option value="IDPS">IDPS</option>
                  <option value="Authentication">Authentication</option>
                  <option value="Custom">Custom</option>
                </select>
              </div>

              {/* Description */}
              <div className="col-span-6">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  rows="3"
                  value={formData.description || ''}
                  onChange={handleChange}
                  className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                ></textarea>
              </div>

              {/* Policy Status */}
              <div className="col-span-6">
                <div className="flex items-center">
                  <input
                    id="is_enabled"
                    name="is_enabled"
                    type="checkbox"
                    checked={formData.is_enabled}
                    onChange={handleChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="is_enabled" className="ml-2 block text-sm text-gray-900">
                    Policy is Enabled
                  </label>
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  When disabled, this policy will not be enforced on any devices.
                </p>
              </div>

              {/* Scope */}
              <div className="col-span-6 sm:col-span-3">
                <label htmlFor="scope_type" className="block text-sm font-medium text-gray-700">
                  Scope Type
                </label>
                <select
                  id="scope_type"
                  name="scope_type"
                  value={formData.scope_type}
                  onChange={handleChange}
                  className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value="global">Global</option>
                  <option value="asset_type">Asset Type</option>
                  <option value="specific_assets">Specific Assets</option>
                  <option value="group">Group</option>
                </select>
              </div>

              {/* Action on Violation */}
              <div className="col-span-6 sm:col-span-3">
                <label htmlFor="action_on_violation" className="block text-sm font-medium text-gray-700">
                  Action on Violation
                </label>
                <select
                  id="action_on_violation"
                  name="action_on_violation"
                  value={formData.action_on_violation}
                  onChange={handleChange}
                  className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value="AlertOnly">Alert Only</option>
                  <option value="Block">Block</option>
                  <option value="Log">Log</option>
                  <option value="Quarantine">Quarantine</option>
                </select>
              </div>

              {/* Scope Identifiers (if not global) */}
              {formData.scope_type !== 'global' && (
                <div className="col-span-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Scope Identifiers
                  </label>
                  <div className="mt-1 space-y-2">
                    {scopeIdentifiers.map((identifier, index) => (
                      <div key={index} className="flex items-center">
                        <input
                          type="text"
                          value={identifier}
                          onChange={(e) => handleScopeIdentifierChange(index, e.target.value)}
                          placeholder={`Enter ${formData.scope_type === 'asset_type' ? 'asset type' : formData.scope_type === 'specific_assets' ? 'asset ID' : 'group name'}`}
                          className="focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                        />
                        <button
                          type="button"
                          onClick={() => removeScopeIdentifier(index)}
                          className="ml-2 inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                        >
                          <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addScopeIdentifier}
                      className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <svg className="mr-1 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Add Identifier
                    </button>
                  </div>
                </div>
              )}

              {/* Rules Section */}
              <div className="col-span-6">
                <h3 className="text-lg font-medium leading-6 text-gray-900 mb-3">Policy Rules</h3>
                {rules.map((rule, index) => (
                  <div key={index} className="mb-6 p-4 border border-gray-200 rounded-md bg-gray-50">
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="text-sm font-medium text-gray-700">Rule #{index + 1}</h4>
                      <button
                        type="button"
                        onClick={() => removeRule(index)}
                        className="inline-flex items-center p-1 border border-transparent rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      >
                        <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    
                    <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                      <div className="sm:col-span-3">
                        <label className="block text-sm font-medium text-gray-700">
                          Rule Name *
                        </label>
                        <input
                          type="text"
                          required
                          value={rule.name}
                          onChange={(e) => handleRuleChange(index, 'name', e.target.value)}
                          className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                        />
                      </div>
                      
                      <div className="sm:col-span-3">
                        <label className="block text-sm font-medium text-gray-700">
                          Action
                        </label>
                        <select
                          value={rule.action}
                          onChange={(e) => handleRuleChange(index, 'action', e.target.value)}
                          className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        >
                          <option value="Alert">Alert</option>
                          <option value="Block">Block</option>
                          <option value="Log">Log</option>
                          <option value="Quarantine">Quarantine</option>
                        </select>
                      </div>
                      
                      <div className="sm:col-span-6">
                        <label className="block text-sm font-medium text-gray-700">
                          Condition *
                        </label>
                        <textarea
                          rows="2"
                          required
                          value={rule.condition}
                          onChange={(e) => handleRuleChange(index, 'condition', e.target.value)}
                          placeholder="E.g., port == 22 && protocol == 'tcp'"
                          className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                        ></textarea>
                      </div>
                      
                      <div className="sm:col-span-6">
                        <label className="block text-sm font-medium text-gray-700">
                          Description
                        </label>
                        <textarea
                          rows="2"
                          value={rule.description || ''}
                          onChange={(e) => handleRuleChange(index, 'description', e.target.value)}
                          className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                        ></textarea>
                      </div>
                    </div>
                  </div>
                ))}
                
                <button
                  type="button"
                  onClick={addRule}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <svg className="mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Add Rule
                </button>
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => navigate('/policies')}
                className="py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className={`inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${submitting ? 'opacity-70 cursor-not-allowed' : ''}`}
              >
                {submitting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </>
                ) : (
                  mode === 'edit' ? 'Update Policy' : 'Create Policy'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PolicyForm;

